/**
 * Shared building blocks for role-based executive dashboards (S05).
 * These components MUST be data-agnostic — they accept props or read
 * from `useTenantData()` so the SAME component renders cleanly in
 * Demo Mode (rich) and Customer Mode (empty / onboarding).
 */
import React from "react";
import { ArrowRight, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

/** Currency formatter — AUD short form (e.g. A$3.4M / A$184k) */
export function fmtAud(value) {
  if (!value && value !== 0) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `A$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `A$${Math.round(value / 1_000)}k`;
  return `A$${Math.round(value)}`;
}

/** Eyebrow label with hairline underline — used at section heads */
export function SectionEyebrow({ children, hint, testId }) {
  return (
    <div className="flex items-end justify-between mb-3" data-testid={testId}>
      <p className="eyebrow">{children}</p>
      {hint && <p className="text-meta text-ink-disabled">{hint}</p>}
    </div>
  );
}

/** KPI tile — restrained, tabular, optional delta and sparkline */
export function KpiTile({
  label,
  value,
  delta,
  deltaTone = "neutral", // 'up' | 'down' | 'neutral'
  hint,
  icon: Icon,
  tone = "tralenex",
  testId,
}) {
  const Trend =
    deltaTone === "up" ? ArrowUpRight : deltaTone === "down" ? ArrowDownRight : Minus;
  const trendClass =
    deltaTone === "up"
      ? "text-risk-low"
      : deltaTone === "down"
        ? "text-risk-high"
        : "text-ink-disabled";

  const toneAccent = {
    tralenex: "before:bg-tralenex-600",
    gold: "before:bg-gold-600",
    risk: "before:bg-risk-high",
    low: "before:bg-risk-low",
    opp: "before:bg-opportunity",
  }[tone];

  return (
    <div
      className={cn(
        "surface-card p-4 relative overflow-hidden shadow-e1",
        "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px]",
        toneAccent,
      )}
      data-testid={testId || `kpi-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow truncate">{label}</p>
        {Icon && <Icon size={13} className="text-ink-secondary" />}
      </div>
      <p className="mt-2 text-display-md text-ink-primary tabular-nums leading-none">
        {value}
      </p>
      <div className="mt-2 flex items-center gap-1.5">
        {delta && (
          <span className={cn("inline-flex items-center gap-0.5 text-meta tabular-nums", trendClass)}>
            <Trend size={11} /> {delta}
          </span>
        )}
        {hint && <span className="text-meta text-ink-secondary truncate">{hint}</span>}
      </div>
    </div>
  );
}

/** Panel card — used for secondary content blocks */
export function PanelCard({ title, action, children, className, testId, footer }) {
  return (
    <div
      className={cn("surface-card shadow-e1 flex flex-col", className)}
      data-testid={testId}
    >
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle flex items-center justify-between gap-3">
        <p className="text-h3 text-ink-primary truncate">{title}</p>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="px-5 py-4 flex-1 min-h-0">{children}</div>
      {footer && (
        <div className="px-5 py-2.5 border-t border-border-subtle bg-inset/40 text-meta text-ink-secondary">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Linear progress bar (e.g. compliance coverage) */
export function LinearProgress({ value, max = 100, tone = "tralenex" }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const bar = {
    tralenex: "bg-tralenex-600",
    gold: "bg-gold-600",
    low: "bg-risk-low",
    risk: "bg-risk-high",
  }[tone];
  return (
    <div className="h-1.5 w-full rounded-full bg-inset overflow-hidden">
      <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Severity chip — high/med/low/shadow */
export function SeverityChip({ severity }) {
  const cls = {
    high: "bg-risk-high/10 text-risk-high",
    med: "bg-risk-med/10 text-risk-med",
    low: "bg-risk-low/10 text-risk-low",
    shadow: "bg-shadow-red/10 text-shadow-red",
  }[severity] || "bg-inset text-ink-secondary";
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
        cls,
      )}
    >
      {severity}
    </span>
  );
}

/** Risk band pill — for agent inventory + radar */
export function RiskBand({ band }) {
  return <SeverityChip severity={band} />;
}

/** Mini sparkline — pure SVG, no deps */
export function Sparkline({ data = [], stroke = "#137DC5", height = 28, width = 96 }) {
  if (!data.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** Section header with optional CTA link */
export function SectionHeader({ eyebrow, title, hint, onAction, actionLabel }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="text-h2 text-ink-primary mt-0.5">{title}</h2>
        {hint && <p className="text-meta text-ink-secondary mt-1">{hint}</p>}
      </div>
      {onAction && actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="inline-flex items-center gap-1 text-meta font-semibold text-tralenex-700 hover:text-tralenex-900 transition-colors"
        >
          {actionLabel} <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}
